import { useState } from 'react';
import { LayoutDashboard, Loader2, Pencil } from 'lucide-react';
import { useAuth } from '@/app/providers/AuthProvider';
import { supabase } from '@/lib/supabase';

/** Papéis que podem editar qualquer post/página do site — espelha a policy `posts_update_member`. */
const EDIT_ROLES = ['super_admin', 'admin', 'editor'];

interface AdminBarProps {
  /** Site sendo visualizado publicamente — pode não ser o site ativo no JWT do usuário. */
  siteId: string;
  /** Post/página atualmente aberta, quando existir uma (ausente em listagens). */
  postId?: string | null;
}

/**
 * Barra de admin estilo WordPress: visível só para quem está logado, com
 * link para o Painel e, quando há uma edição possível, para editar a
 * página/post aberto. Fica presa no topo para manter os atalhos de edição
 * acessíveis durante a rolagem do site público.
 */
export function AdminBar({ siteId, postId }: AdminBarProps) {
  const { session, user, role, isSuperAdmin } = useAuth();
  const [switching, setSwitching] = useState(false);

  if (!session) return null;

  // app_metadata.site_id é o site ativo no JWT (o que current_site_id() lê no
  // RLS) — pode ser diferente do site que a pessoa está navegando aqui.
  const activeSiteId = (user?.app_metadata?.site_id as string | undefined) ?? null;
  const sameSite = activeSiteId === siteId;
  const canEdit = !!postId && !!role && EDIT_ROLES.includes(role) && (isSuperAdmin || sameSite);

  const openEditor = async () => {
    if (!postId) return;
    // Super admin pode editar qualquer site, mas o RLS só autoriza no site
    // ativo do JWT — troca antes de navegar para não cair em "não encontrado".
    if (isSuperAdmin && !sameSite) {
      setSwitching(true);
      try {
        const { error } = (await supabase.functions.invoke('switch-site', {
          body: { site_id: siteId },
        })) as { data: unknown; error: Error | null };
        if (error) throw error;
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) throw refreshError;
      } catch {
        setSwitching(false);
        return;
      }
    }
    window.location.assign(`/posts/${postId}`);
  };

  return (
    <div className="sticky top-0 z-[1000] flex w-full items-center justify-end gap-1 bg-slate-900 px-4 py-1.5 text-xs font-medium text-slate-200 shadow-sm">
      {canEdit && (
        <button
          type="button"
          onClick={() => void openEditor()}
          disabled={switching}
          className="inline-flex items-center gap-1.5 rounded px-2.5 py-1 transition-colors hover:bg-white/10 disabled:opacity-60"
        >
          {switching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Pencil className="h-3.5 w-3.5" />}
          Editar
        </button>
      )}
      <a href="/app" className="inline-flex items-center gap-1.5 rounded px-2.5 py-1 transition-colors hover:bg-white/10">
        <LayoutDashboard className="h-3.5 w-3.5" />
        Painel
      </a>
    </div>
  );
}
